import axios from 'axios'
import { ElMessage } from 'element-plus'
import store from '@/store'
import router from '@/router'

// 创建axios实例
const BASE = process.env.VUE_APP_API_BASE_URL || '/api'

const service = axios.create({
  baseURL: BASE,
  timeout: 10000
})

// 临时用：从请求拦截器同步 Vuex 里的 token 到本地（登录后 commit 已同步，无需额外处理）

// ---------- 401 自动续期（并发安全：单飞 + 排队重放） ----------
let isRefreshing = false // 是否正在刷新（并发多个 401 时只发起一次刷新）
let pendingQueue = [] // 刷新期间等待重放的请求
let authFailedNotified = false // 同一轮过期只提示一次，避免“一堆登录已过期”

// 用原生 axios 调刷新接口，避免走本实例拦截器造成递归
function refreshAccessToken () {
  const refreshToken = localStorage.getItem('refresh_token')
  if (!refreshToken) {
    return Promise.reject(new Error('刷新令牌不存在'))
  }
  return axios
    .post(`${BASE}/auth/refresh`, { refresh_token: refreshToken }, { timeout: 10000 })
    .then(res => {
      if (!res.data || !res.data.access_token) {
        throw new Error('刷新失败')
      }
      return res.data.access_token
    })
}

function applyToken (config, token) {
  config.headers = config.headers || {}
  config.headers['Authorization'] = `Bearer ${token}`
}

function clearSession () {
  store.commit('user/SET_TOKEN', '')
  store.commit('user/SET_USER_INFO', null)
  store.commit('user/SET_PERMISSIONS', [])
  store.commit('user/SET_ROLES', [])
  localStorage.removeItem('access_token')
  localStorage.removeItem('refresh_token')
}

// 登录/刷新本身失败不触发续期
function shouldRefresh (config) {
  const url = (config && config.url) || ''
  return !url.includes('/auth/login') && !url.includes('/auth/refresh')
}

// 处理 401：尝试自动续期，成功则重放排队请求与本请求
function handleUnauthorized (error) {
  const config = error && error.config
  if (!config || config._retry || !shouldRefresh(config)) {
    return Promise.reject(error)
  }

  config._retry = true

  // 已有刷新在进行中，把当前请求排队等待
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      pendingQueue.push({ config, resolve, reject })
    })
  }

  isRefreshing = true
  return refreshAccessToken()
    .then(newToken => {
      store.commit('user/SET_TOKEN', newToken)
      localStorage.setItem('access_token', newToken)
      authFailedNotified = false // 续期成功，重置提示标记

      // 重放刷新期间排队的请求
      pendingQueue.forEach(({ config: pendingConfig, resolve, reject }) => {
        applyToken(pendingConfig, newToken)
        service(pendingConfig).then(resolve).catch(reject)
      })
      pendingQueue = []

      applyToken(config, newToken)
      return service(config)
    })
    .catch(err => {
      pendingQueue.forEach(({ reject }) => reject(err))
      pendingQueue = []

      // 刷新失败（刷新令牌也过期/不存在）：统一清会话并只提示一次
      clearSession()
      if (!authFailedNotified) {
        authFailedNotified = true
        ElMessage.error('登录已过期，请重新登录')
        if (router.currentRoute.value.path !== '/login') {
          router.replace('/login')
        }
      }
      return Promise.reject(err)
    })
    .finally(() => {
      isRefreshing = false
    })
}

// 请求拦截器
service.interceptors.request.use(
  config => {
    // 添加token到请求头
    const token = store.getters['user/token']
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }

    // 添加内容类型
    if (!config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json'
    }

    return config
  },
  error => {
    console.error('请求错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    const res = response.data

    // 如果返回的是文件流，直接返回
    if (response.config.responseType === 'blob') {
      return response
    }

    // FastAPI使用标准的HTTP状态码，不包装业务状态码
    // 所以这里不检查res.code，直接返回数据
    return res
  },
  error => {
    console.error('响应错误:', error)

    // 401：走自动续期逻辑（刷新失败时才提示“登录已过期”并跳转）
    if (error.response && error.response.status === 401) {
      return handleUnauthorized(error)
    }

    if (error.response) {
      const { status, data } = error.response

      switch (status) {
        case 403:
          ElMessage.error('权限不足')
          break
        case 404:
          ElMessage.error('请求的资源不存在')
          break
        case 500:
          ElMessage.error('服务器内部错误')
          break
        default:
          // FastAPI使用detail字段而不是message字段
          ElMessage.error(data?.detail || data?.message || '请求失败')
      }
    } else if (error.request) {
      ElMessage.error('网络连接失败，请检查网络')
    } else {
      ElMessage.error('请求配置错误')
    }

    return Promise.reject(error)
  }
)

export default service
